import {IBrowser, IEventManagerWithAsync} from '@gongxigames/framework/core/types';
import {SignalEventEmitterWrapper} from '@gongxigames/framework/core/services/strictEventEmitter';
import {StrictUiEmitterEventMap} from '@gongxigames/framework/slotV2/globals';

interface IPostMessage {
    type: string | symbol | number;
    sender: string;
    lang: string;
    data?: any;
}

export abstract class PostMessageHandler<MessagesMap extends {}> {
    static NAME = 'PostMessageHandler';
    protected _sender: string;
    protected _lang: string;
    protected _eventManager: IEventManagerWithAsync;
    protected _uiEmitter: SignalEventEmitterWrapper<StrictUiEmitterEventMap>;
    protected _browser: IBrowser;

    constructor(
        eventManager: IEventManagerWithAsync,
        uiEmitter: SignalEventEmitterWrapper<StrictUiEmitterEventMap>,
        browser: IBrowser,
        gameCode: string,
        languageCode: string
    ) {
        this._eventManager = eventManager;
        this._uiEmitter = uiEmitter;
        this._browser = browser;
        this._sender = gameCode;
        this._lang = languageCode;

        this.setupListeners();
        this.setupEvents();
    }

    protected abstract setupEvents(): void;

    protected abstract setupListeners(): void;

    protected postMessage<K extends keyof MessagesMap>(type: K, data?: MessagesMap[K]): void {
        const message: IPostMessage = {
            type,
            sender: this._sender,
            lang: this._lang
        };

        if (data) {
            message.data = data;
        }

        if (window.parent) {
            this._browser.postMessageToParent(message, '*');
        } else {
            this._browser.postMessage(message, '*');
        }
    }

    protected postMessageWithDataToExtract<K extends keyof MessagesMap>(type: K, data?: MessagesMap[K]): void {
        const message: IPostMessage = {
            type,
            sender: this._sender,
            lang: this._lang
        };

        if (data) {
            Object.assign(message, data);
        }

        if (window.parent) {
            this._browser.postMessageToParent(message, '*');
        } else {
            this._browser.postMessage(message, '*');
        }
    }

    protected receiveMessage(event: any): void {
        if (event.data && event.data.type) {
            this._eventManager.emit(event.data.type, PostMessageHandler.NAME);
        }
    }

    protected on(id: string | symbol, listener: Function, priority?: number): void {
        this._eventManager.on(id, listener.bind(this, id), this, priority);
    }
}
